import { useEffect, useRef, useState } from 'react';
import { Barcode, Loader2, Trash2, ExternalLink, Paperclip } from 'lucide-react';
import { Modal } from '../Modal';
import { api, getToken } from '../../lib/api';
import { formatMoeda, formatDataSP, normalizarLinhaDigitavel, linhaDigitavelCabe, lerLinhaDigitavel } from '@fiodecobre/shared';

// Anexar o boleto a UM OU MAIS titulos, pra ele aparecer no AppCel ([DA-629]/[DA-630]).
//
// ⛔ **Nada aqui GERA boleto.** O Ruan emite no painel do banco, como sempre — isto
// so guarda o PDF e o entrega ao cliente pelo canal proprio, no lugar do WhatsApp.
//
// 🔑 **O NUMERO DA NOTA NAO SE DIGITA** (27/08/2026). Ele pediu um campo pra informar a
// nota; o campo era remendo pro agrupamento que faltava — ele junta varias notas num
// boleto so e nao tinha como marcar os titulos. Marcando, a nota de cada um sai da
// ligacao que a casa ja tinha (titulo → orcamento → nota, [DA-590]). Digitar criaria uma
// segunda verdade que nada delata: 243 escrito num boleto da 245, e o cliente lancando
// errado no contas a pagar dele.
//
// ⛔ **Salvar NAO avisa o cliente** (decisao dele, 20/10/2026: "o aviso deve ser so no
// vencimento"). O botao era "Salvar e avisar o cliente" e disparava push no celular; o
// unico aviso agora e' o lembrete de WhatsApp do dia do vencimento. A faixa depois de
// salvar diz isso, e diz quando o cliente nao tem conta no app (o boleto nao aparece la).

/** Um titulo que o operador marcou na lista. */
export interface TituloDoBoleto {
  id: number;
  cliente_nome: string;
  valor: string;
  data_vencimento: string;
}

interface NotaDoTitulo { tipo: 'nfe' | 'nfce'; numero: number }

interface TituloCoberto {
  id: number;
  valor: number;
  data_vencimento: string | null;
  orcamento_numero: string | null;
  nota_tipo: 'nfe' | 'nfce' | null;
  nota_numero: number | null;
}

interface BoletoAtual {
  id: number;
  cliente_nome: string;
  data_vencimento: string | null;
  valor: number;
  /** `null` = boleto so com a linha (PDF opcional desde 20/10/2026). */
  arquivo_bytes: number | null;
  nome_original: string | null;
  linha_digitavel: string | null;
  criado_em: string | null;
  usuario_nome: string | null;
  titulos: TituloCoberto[];
}

/** O desfecho do salvar: `tem_conta_app` null = a consulta falhou (a tela nao afirma). */
interface Aviso {
  tem_conta_app: boolean | null;
}

/** Teto do lado do cliente, espelhando o do servidor — recusar aqui poupa subir 5MB
 *  pra levar 413 depois. ⚠️ O servidor continua conferindo: trava de tela nunca e' a
 *  unica (e nunca pode ser mais rigida que a de la). */
const BYTES_MAX = 5 * 1024 * 1024;

/** O rotulo da nota de um titulo — ou a porta que sobra quando ele nao tem nota. */
function rotuloNota(t: Pick<TituloCoberto, 'nota_tipo' | 'nota_numero' | 'orcamento_numero'>): string {
  if (t.nota_numero !== null) return `${t.nota_tipo === 'nfce' ? 'Cupom' : 'NF-e'} ${t.nota_numero}`;
  // ⚠️ Titulo sem nota e' caso REAL (9 dos 73 pendentes, quase todos de consignacao).
  // O orcamento no lugar diz de que compra e' — traco mudo faria parecer defeito.
  return t.orcamento_numero ? `${t.orcamento_numero} (sem nota)` : 'sem nota';
}

export function ModalBoletoTitulo({ titulos, onFechar, onSalvo }: {
  titulos: TituloDoBoleto[] | null;
  onFechar: () => void;
  onSalvo: () => void;
}): React.ReactElement | null {
  const [atual, setAtual] = useState<BoletoAtual | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [linha, setLinha] = useState('');
  const [vencimento, setVencimento] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<Aviso | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 🔴 **A dependencia e' a CHAVE dos ids, nunca o array `titulos`** — isto foi achado
  // apertando o botao em prod, na primeira release. O pai monta a lista INLINE no JSX,
  // entao ela nasce com identidade nova a cada render; e como `onSalvo` recarrega a
  // lista, salvar re-renderizava o pai, o efeito rodava de novo e o `setAviso(null)`
  // daqui **apagava o veredito do push que o operador tinha acabado de produzir**.
  // Nada falhava: so a faixa que diz se o cliente foi avisado sumia.
  const chave = titulos?.map((t) => t.id).join(',') ?? '';

  useEffect(() => {
    if (!chave) return;
    let vivo = true;
    setCarregando(true);
    setErro(null);
    setAviso(null);
    setArquivo(null);

    // ⚠️ O vencimento nasce com o MAIS DISTANTE entre os marcados. Juntando titulos que
    // vencem 03, 04 e 05/09, herdar o primeiro faria o app dizer "vencido" antes da
    // data que a propria loja deu ao cliente. Com um titulo so, ninguem digita nada.
    const datas = (titulos ?? []).map((t) => t.data_vencimento).filter(Boolean).sort();
    setVencimento(datas.length ? String(datas[datas.length - 1]).slice(0, 10) : '');

    // Basta perguntar pelo PRIMEIRO: o boleto que cobre qualquer um dos marcados vem
    // inteiro, com todos os titulos dele.
    api.get<{ boleto: BoletoAtual | null }>(`/api/contas-receber/${chave.split(',')[0]}/boleto`)
      .then((r) => {
        if (!vivo) return;
        setAtual(r.boleto);
        setLinha(r.boleto?.linha_digitavel ?? '');
        if (r.boleto?.data_vencimento) setVencimento(String(r.boleto.data_vencimento).slice(0, 10));
      })
      .catch((e: Error) => { if (vivo) setErro(e.message); })
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
  }, [chave]);

  if (!titulos || !titulos.length) return null;

  const soma = titulos.reduce((s, t) => s + Number(t.valor || 0), 0);
  const varios = titulos.length > 1;

  function escolher(f: File | null): void {
    setErro(null);
    if (!f) { setArquivo(null); return; }
    // ⚠️ Confere pelo TIPO e pela extensao: alguns navegadores mandam `type` vazio
    // pra arquivo vindo de gerenciador de arquivos, e recusar ali seria barrar o
    // caminho normal de quem baixou o boleto no computador.
    const ehPdf = f.type === 'application/pdf' || /\.pdf$/i.test(f.name);
    if (!ehPdf) { setErro('O boleto precisa ser um arquivo PDF.'); return; }
    if (f.size > BYTES_MAX) { setErro('PDF grande demais (limite de 5 MB).'); return; }
    setArquivo(f);
  }

  async function salvar(): Promise<void> {
    if (salvando || !titulos) return;
    setSalvando(true);
    setErro(null);
    setAviso(null);
    try {
      // PDF opcional desde 20/10/2026 (pedido dele): a linha digitavel e' o que paga.
      const base64 = arquivo ? await new Promise<string>((resolve, reject) => {
        const leitor = new FileReader();
        leitor.onerror = () => reject(new Error('Nao consegui ler o arquivo.'));
        // `readAsDataURL` devolve `data:application/pdf;base64,XXXX` — o servidor ja
        // sabe descartar o prefixo (mesmo caminho da foto do chat).
        leitor.onload = () => resolve(String(leitor.result ?? '').split(',')[1] ?? '');
        leitor.readAsDataURL(arquivo);
      }) : null;

      const r = await api.post<{ id: number; tem_conta_app: boolean | null }>('/api/contas-receber/boletos', {
        titulo_ids: titulos.map((t) => t.id),
        pdf_base64: base64,
        nome_arquivo: arquivo?.name ?? null,
        linha_digitavel: linha || null,
        data_vencimento: vencimento,
      });
      setAviso({ tem_conta_app: r.tem_conta_app });
      setArquivo(null);
      if (inputRef.current) inputRef.current.value = '';
      // Rele o estado gravado em vez de montar na mao o que EU acho que ficou la —
      // e' o servidor que decide o nome guardado, a hora, quem subiu e as notas.
      const atualizado = await api.get<{ boleto: BoletoAtual | null }>(`/api/contas-receber/${titulos[0]!.id}/boleto`);
      setAtual(atualizado.boleto);
      onSalvo();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  async function remover(): Promise<void> {
    if (salvando || !titulos) return;
    setSalvando(true);
    setErro(null);
    setAviso(null);
    try {
      await api.delete(`/api/contas-receber/${titulos[0]!.id}/boleto`);
      setAtual(null);
      setLinha('');
      onSalvo();
    } catch (e) {
      setErro((e as Error).message);
    } finally {
      setSalvando(false);
    }
  }

  /** O PDF sai por rota autenticada, entao nao da' pra abrir por `<a href>` puro —
   *  o token vive no cabecalho. Busca por `fetch` e abre um blob. */
  async function verPdf(): Promise<void> {
    if (!titulos) return;
    try {
      const resp = await fetch(`/api/contas-receber/${titulos[0]!.id}/boleto/pdf`, {
        headers: { Authorization: `Bearer ${getToken() ?? ''}` },
      });
      if (!resp.ok) throw new Error('Nao consegui abrir o arquivo.');
      const url = URL.createObjectURL(await resp.blob());
      // ⛔ Sem `noopener` no 3o argumento: com ele o `window.open` devolve `null`
      // SEMPRE e o idioma de fallback dispara a toa ([DA-572]). A protecao volta na
      // linha seguinte, sem perder o retorno.
      const janela = window.open(url, '_blank');
      if (janela) janela.opener = null;
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  // Mesma peca que o servidor usa pra recusar — a trava de tela nunca pode ser mais
  // rigida (nem mais frouxa) que a de la.
  const digitos = normalizarLinhaDigitavel(linha);
  const linhaValida = linhaDigitavelCabe(digitos);
  // A linha e' OBRIGATORIA desde que o PDF virou opcional (20/10/2026) — e o servidor
  // recusa igual, entao a tela nao e' mais rigida que ele.
  const podeSalvar = Boolean(digitos) && linhaValida && Boolean(vencimento) && !salvando;
  // Banco, valor e vencimento que a PROPRIA linha diz — e' o que o banco vai cobrar.
  const daLinha = lerLinhaDigitavel(digitos);
  const valorDiverge = daLinha?.valor != null
    && Math.round(daLinha.valor * 100) !== Math.round(soma * 100);
  const vencDiverge = Boolean(daLinha?.vencimento && vencimento && daLinha.vencimento !== vencimento);
  // Sem PDF novo, o servidor so herda o PDF atual se for o MESMO papel (mesma linha, ou
  // a antiga vazia) — a tela diz antes de salvar o que vai acontecer com ele.
  const pdfAtualFica = Boolean(atual?.arquivo_bytes != null && !arquivo
    && (!atual?.linha_digitavel || atual.linha_digitavel === digitos));

  function mudarLinha(bruto: string): void {
    const limpo = bruto.replace(/\D/g, '');
    setLinha(limpo);
    // O vencimento acompanha a linha: e' a data do banco, sem ajuste a mao.
    const lido = lerLinhaDigitavel(limpo);
    if (lido?.vencimento) setVencimento(lido.vencimento);
  }

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura={600}
      titulo={atual ? 'Boleto do cliente' : varios ? `Anexar boleto a ${titulos.length} titulos` : 'Anexar boleto ao titulo'}
      rodape={
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* ⚠️ Botao desabilitado DIZ o que falta — apagado e mudo e' beco sem saida. */}
          {!digitos && (
            <span style={{ fontSize: 12, color: 'var(--text-muted)', marginRight: 'auto' }}>
              Cole a linha digitavel para habilitar o envio.
            </span>
          )}
          {digitos && linhaValida && !vencimento && (
            <span style={{ fontSize: 12, color: '#b91c1c', marginRight: 'auto' }}>
              Informe o vencimento do boleto.
            </span>
          )}
          {/* ⚠️ O erro DIZ QUANTOS ele tem, nao so' o alvo — foi o que faltou em 08/09/2026,
              quando o valor do boleto (R$ 587,00 → 5 digitos) foi colado no lugar da linha e a
              tela so' repetia o alvo. A contagem existia, mas em cinza do outro lado do modal:
              quem le o aviso vermelho nao a alcancava. Curto de proposito — o rodape divide a
              linha com dois botoes, e a SAIDA ("apague o campo") mora ao lado do campo, que e'
              onde a mao age. */}
          {digitos && !linhaValida && (
            <span style={{ fontSize: 12, color: '#b91c1c', marginRight: 'auto' }}>
              Voce colou {digitos?.length ?? 0} {digitos?.length === 1 ? 'digito' : 'digitos'} — a linha tem 47 ou 48.
            </span>
          )}
          <button type="button" className="btn-secundario" onClick={onFechar}>Fechar</button>
          <button
            type="button"
            className="btn-primario"
            disabled={!podeSalvar}
            onClick={() => { void salvar(); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}
          >
            {salvando ? <Loader2 size={15} className="animate-spin" /> : <Barcode size={15} />}
            {atual ? 'Substituir boleto' : 'Salvar boleto'}
          </button>
        </div>
      }
    >
      <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.5 }}>
        <b style={{ color: 'var(--text-color)' }}>{titulos[0]!.cliente_nome}</b>
        {' · '}
        {varios ? `${titulos.length} titulos · total ` : ''}
        <b style={{ color: 'var(--text-color)' }}>{formatMoeda(soma)}</b>
        <div style={{ marginTop: 4 }}>
          O cliente ve este boleto na aba <b>Boletos</b> do aplicativo, e ele sai de la sozinho
          quando {varios ? 'o ultimo destes titulos for baixado' : 'o titulo for baixado'}.
        </div>
      </div>

      {carregando && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Carregando...</div>}

      {!carregando && atual && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 14, background: 'var(--panel-bg)' }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>
            Boleto no ar — {formatMoeda(atual.valor)}, vence {atual.data_vencimento ? formatDataSP(atual.data_vencimento) : '—'}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            {atual.arquivo_bytes != null
              ? `${atual.nome_original ?? 'boleto.pdf'} · ${Math.max(1, Math.round(atual.arquivo_bytes / 1024))} KB`
              : 'Sem PDF — so a linha digitavel'}
            <br />
            Enviado {atual.criado_em ? formatDataSP(atual.criado_em) : '—'}
            {atual.usuario_nome ? ` por ${atual.usuario_nome}` : ''}
          </div>
          {/* 🔑 As notas que ele queria digitar — derivadas, uma por titulo coberto. */}
          <div style={{ marginTop: 8, fontSize: 12.5 }}>
            <span style={{ color: 'var(--text-muted)' }}>
              {atual.titulos.length > 1 ? `Cobre ${atual.titulos.length} titulos:` : 'Referente a:'}
            </span>{' '}
            {atual.titulos.map((t, i) => (
              <span key={t.id}>
                {i > 0 && ' · '}
                <b>{rotuloNota(t)}</b> <span style={{ color: 'var(--text-muted)' }}>({formatMoeda(t.valor)})</span>
              </span>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {atual.arquivo_bytes != null && (
              <button type="button" className="btn-secundario" onClick={() => { void verPdf(); }}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                <ExternalLink size={14} /> Ver o PDF
              </button>
            )}
            <button type="button" className="btn-secundario" onClick={() => { void remover(); }} disabled={salvando}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', color: '#b91c1c' }}>
              <Trash2 size={14} /> Tirar do aplicativo
            </button>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: 12 }}>
        <div>
          <label className="form-label">Linha digitavel</label>
          <input
            type="text"
            value={linha}
            onChange={(e) => mudarLinha(e.target.value)}
            inputMode="numeric"
            placeholder="Cole aqui os numeros do boleto"
            style={{ width: '100%', fontVariantNumeric: 'tabular-nums' }}
          />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>
            {/* O que a propria linha diz — o operador confere contra o papel na hora. */}
            {daLinha ? (
              <b style={{ color: 'var(--text-color)' }}>
                {daLinha.banco} · {daLinha.valor != null ? formatMoeda(daLinha.valor) : 'sem valor'}
                {' · '}{daLinha.vencimento ? `vence ${formatDataSP(daLinha.vencimento)}` : 'sem vencimento'}
              </b>
            ) : 'O cliente copia e cola no aplicativo do banco com um toque.'}
            {digitos ? ` (${digitos.length} dígitos)` : ''}
          </div>
        </div>
        <div>
          <label className="form-label">Vencimento do boleto</label>
          <input type="date" value={vencimento} onChange={(e) => setVencimento(e.target.value)}
            style={{ width: '100%' }} />
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>
            {daLinha?.vencimento && !vencDiverge ? 'Lido da linha digitavel.'
              : varios ? 'Veio do titulo que vence por ultimo.' : 'Veio do titulo.'}
          </div>
        </div>
      </div>

      {/* Avisa, nao trava: pega a linha de OUTRO boleto colada por engano (o mesmo
          cliente costuma ter varios), mas a decisao de salvar assim e' do operador. */}
      {(valorDiverge || vencDiverge) && (
        <div style={{ marginTop: 10, background: '#ffedd5', border: '1px solid #f9c99a', color: '#000', borderRadius: 8, padding: 10, fontSize: 13, lineHeight: 1.5 }}>
          {valorDiverge && (
            <div>A linha diz <b>{formatMoeda(daLinha!.valor!)}</b>, mas {varios ? 'os títulos somam' : 'o título é'} <b>{formatMoeda(soma)}</b>. Confira se colou a linha do boleto certo.</div>
          )}
          {vencDiverge && (
            <div>A linha diz que vence em <b>{formatDataSP(daLinha!.vencimento!)}</b>, e o vencimento informado é <b>{formatDataSP(vencimento)}</b>.</div>
          )}
        </div>
      )}

      <div style={{ marginTop: 14 }}>
        <label className="form-label">{atual?.arquivo_bytes != null ? 'Trocar o PDF (opcional)' : 'PDF do boleto (opcional)'}</label>
        {/* Input nativo escondido: o "Nenhum arquivo escolhido" do navegador, ao lado de um
            boleto que JA tem PDF, era lido como "o PDF nao foi anexado" (26/10/2026). */}
        <input ref={inputRef} type="file" accept="application/pdf,.pdf" className="hidden"
          onChange={(e) => escolher(e.target.files?.[0] ?? null)} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <button type="button" className="btn-secundario" onClick={() => inputRef.current?.click()} disabled={salvando}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
            <Paperclip size={14} /> {atual?.arquivo_bytes != null || arquivo ? 'Escolher outro PDF' : 'Escolher o PDF'}
          </button>
          {arquivo && (
            <span style={{ fontSize: 13 }}>
              <b>{arquivo.name}</b> · {Math.max(1, Math.round(arquivo.size / 1024))} KB{' '}
              <button type="button" style={{ fontSize: 12.5, marginLeft: 6, color: 'var(--primary)', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}
                onClick={() => { escolher(null); if (inputRef.current) inputRef.current.value = ''; }}>
                desistir
              </button>
            </span>
          )}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5 }}>
          {atual?.arquivo_bytes != null && !arquivo
            ? (pdfAtualFica ? `Sem PDF novo, o PDF atual (${atual.nome_original ?? 'boleto.pdf'}) continua junto.`
              : 'A linha mudou: o PDF atual sai. Anexe o novo se o cliente precisar do documento.')
            : 'So para o cliente que pede o documento — a linha ja basta para pagar.'}
        </div>
      </div>

      {/* Os titulos que vao neste boleto — com a NOTA de cada um, pra conferir antes de
          enviar que a marcacao esta certa. E' o que substitui o campo digitado. */}
      {!atual && (
        <div style={{ marginTop: 14, border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <div style={{ padding: '8px 10px', background: 'var(--panel-bg)', fontSize: 12.5, fontWeight: 700 }}>
            {varios ? `Este boleto cobre ${titulos.length} titulos` : 'Este boleto cobre'}
          </div>
          {titulos.map((t) => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '7px 10px', fontSize: 12.5, borderTop: '1px solid var(--border)' }}>
              <span style={{ color: 'var(--text-muted)' }}>
                titulo #{t.id} · vence {formatDataSP(t.data_vencimento)}
              </span>
              <span className="cel-num">{formatMoeda(t.valor)}</span>
            </div>
          ))}
          {varios && (
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', fontSize: 13, fontWeight: 700, borderTop: '1px solid var(--border)', background: 'var(--panel-bg)' }}>
              <span>Total do boleto</span>
              <span className="cel-num-forte">{formatMoeda(soma)}</span>
            </div>
          )}
        </div>
      )}

      {erro && (
        <div style={{ marginTop: 14, background: '#fef2f2', border: '1px solid #fecaca', color: '#7f1d1d', borderRadius: 8, padding: 10, fontSize: 13 }}>
          {erro}
        </div>
      )}

      {/* O desfecho do salvar: nada foi enviado agora, e o cliente sem conta no app nao
          ve o boleto la — isso a tela diz na hora, com a porta pra resolver. */}
      {aviso && (
        <div style={{
          marginTop: 14, borderRadius: 8, padding: 10, fontSize: 13, lineHeight: 1.5,
          background: aviso.tem_conta_app === false ? '#ffedd5' : '#fde68a',
          border: `1px solid ${aviso.tem_conta_app === false ? '#f9c99a' : '#d6b64a'}`,
          color: '#000',
        }}>
          <b>Boleto salvo.</b> O cliente recebe o lembrete no WhatsApp no dia do vencimento.
          {aviso.tem_conta_app === false && (
            <> Este cliente <b>ainda nao tem conta no aplicativo</b> — ele nao vai ver o boleto la.
              Convide-o pelo painel do contato, no Atendimento.</>
          )}
        </div>
      )}
    </Modal>
  );
}
